import { useState } from 'react';
import { Box, Typography, Paper, Button, CircularProgress } from '@mui/material';
import { ArrowBack as BackIcon, Save as SaveIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import ErpClientFormFields, {
  emptyClientForm,
  applyFieldChange,
  toClientPayload,
  clientFormatErrors,
} from './ErpClientFormFields';

/**
 * Creación de cliente ERP: la "ficha de cliente" digital que reemplaza la
 * ficha Excel homologada (decisión 7 — nada por Excel).
 */
const ErpClientWizard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken, showAlert } = useStore.getState();

  const [form, setForm] = useState(emptyClientForm);
  const [saving, setSaving] = useState(false);

  const handleFieldChange = (path, value) =>
    setForm((prev) => applyFieldChange(prev, path, value));

  const handleSave = async () => {
    if (!form.legalName.trim() || !form.rut.trim()) {
      showAlert(t('erp.common.requiredFields'), 'warning');
      return;
    }
    if (Object.keys(clientFormatErrors(form)).length > 0) {
      showAlert(t('erp.validation.fixErrors'), 'warning');
      return;
    }

    try {
      setSaving(true);

      const response = await api.post(
        '/erp/clients',
        { ...toClientPayload(form), organizationId: organization },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.clients.createSuccess'), 'success');
        const uid = response.data.response.data?.uid;
        navigate(uid ? `/erp/clients/${uid}` : '/erp/clients');
      } else {
        showAlert(t('erp.clients.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errorMessage || '';
      if (message.includes('Invalid RUT')) {
        showAlert(t('erp.clients.invalidRut'), 'error');
      } else if (message.includes('already exists')) {
        showAlert(t('erp.clients.duplicateRut'), 'error');
      } else {
        showAlert(t('erp.clients.saveError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/erp/clients')} sx={{ textTransform: 'none' }}>
          {t('erp.clients.title')}
        </Button>
      </Box>

      <Typography variant="h4" fontWeight={700}>{t('erp.clients.createTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('erp.clients.createSubtitle')}
      </Typography>

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3 }}>
        <ErpClientFormFields form={form} onChange={handleFieldChange} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button onClick={() => navigate('/erp/clients')} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={saving || Object.keys(clientFormatErrors(form)).length > 0}
            onClick={handleSave}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
          >
            {saving ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ErpClientWizard;
